'use strict';

/**
 * 管理端服务端根地址解析（TASK-005，R1 修复）。
 *
 * VUE_APP_BASE_URL 的语义是“服务端根地址”，例如 https://api.example.com，
 * 不能带 /api，也不能带路径、查询或片段；axios 请求本身还会再拼接 /api。
 *
 * - 开发（development）：未配置时只回落到显式 loopback 代理目标
 *   http://127.0.0.1:1002；浏览器侧一律使用相对地址，由 devServer 代理转发，
 *   因此不会把跨域问题带到浏览器。
 * - 生产（production）：必须显式配置；绝对地址必须是 https，或用 “/”
 *   显式声明同源部署。缺失或非法时直接抛错，使构建失败，绝不回落到任何
 *   远端默认地址或 undefined。
 *
 * mode 语义（R1）：production 只由实际的 NODE_ENV 决定，不能用 staging 等
 * CLI mode 别名把生产构建降级为开发；vue.config.js 与 src 内的 clientBase
 * 使用同一个 mode 来源，保证构建门禁与运行时一致。
 *
 * 地址校验使用浏览器与 Node 都支持的标准 URL 解析，并补充必要的原输入检查
 * （反斜杠、空白），不依赖 Node 专有 API，也不引入外部校验库。错误信息只说明
 * 变量名与错误类别，不回显配置值，避免把地址、凭据等写入日志。
 */

const ENV_NAME = 'VUE_APP_BASE_URL';
const DEFAULT_DEVELOPMENT_BASE_URL = 'http://127.0.0.1:1002';
const COMPILE_ONLY_PLACEHOLDER_HOST = 'example.invalid';
const SAME_ORIGIN_VALUE = '/';

class EnvConfigError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'EnvConfigError';
    this.code = code;
  }
}

function readEnvValue(value) {
  return value === undefined || value === null ? '' : String(value).trim();
}

function trimTrailingSlash(value) {
  return String(value).replace(/\/+$/, '');
}

function fail(code, detail) {
  throw new EnvConfigError(code, `invalid ${ENV_NAME}: ${detail}`);
}

function parseAbsoluteBaseUrl(value) {
  if (/\s/.test(value)) {
    fail('WHITESPACE', 'must not contain whitespace');
  }
  if (value.indexOf('\\') !== -1) {
    fail('AUTHORITY', 'authority must not contain backslashes');
  }
  if (!/^https?:\/\//i.test(value)) {
    fail('NOT_ABSOLUTE', 'must be an absolute http(s) URL or "/" for same origin');
  }

  let parsed;
  try {
    parsed = new URL(value);
  } catch {
    // 非数字端口、端口越界、IPv6 括号后多余文本等都会在这里被拒绝
    fail('NOT_ABSOLUTE', 'must be an absolute http(s) URL or "/" for same origin');
  }

  const scheme = parsed.protocol.replace(/:$/, '').toLowerCase();
  if (scheme !== 'http' && scheme !== 'https') {
    fail('NOT_ABSOLUTE', 'must be an absolute http(s) URL or "/" for same origin');
  }
  if (parsed.username || parsed.password) {
    fail('USERINFO', 'credentials in URL are not allowed');
  }
  if (parsed.search) {
    fail('QUERY', 'query strings are not allowed');
  }
  if (parsed.hash) {
    fail('FRAGMENT', 'fragments are not allowed');
  }
  if (trimTrailingSlash(parsed.pathname) !== '') {
    fail('PATH', 'must not contain a path, in particular not /api');
  }
  if (parsed.hostname === '') {
    fail('HOST', 'host is missing');
  }
  return {scheme, hostname: parsed.hostname, origin: `${parsed.protocol}//${parsed.host}`};
}

function isCompileOnlyPlaceholder(hostname) {
  const normalized = hostname.replace(/^\[|\]$/g, '').toLowerCase();
  return normalized === COMPILE_ONLY_PLACEHOLDER_HOST || normalized.endsWith(`.${COMPILE_ONLY_PLACEHOLDER_HOST}`);
}

function resolveConsoleEndpoints(rawValue, options) {
  const mode = (options && options.mode) || 'development';
  const isProduction = mode === 'production';
  const value = readEnvValue(rawValue);

  if (value === '') {
    if (isProduction) {
      throw new EnvConfigError('MISSING', `${ENV_NAME} is required for production build (use "/" for same-origin)`);
    }
    const devDefault = parseAbsoluteBaseUrl(DEFAULT_DEVELOPMENT_BASE_URL);
    return {
      origin: devDefault.origin,
      clientBase: '',
      proxyTarget: devDefault.origin,
      isSameOrigin: false,
      isCompileOnlyPlaceholder: false
    };
  }

  if (value === SAME_ORIGIN_VALUE) {
    if (!isProduction) {
      throw new EnvConfigError(
        'SAME_ORIGIN_IN_DEVELOPMENT',
        `development ${ENV_NAME} must be an absolute proxy target, not same-origin`
      );
    }
    return {
      origin: '',
      clientBase: '',
      proxyTarget: null,
      isSameOrigin: true,
      isCompileOnlyPlaceholder: false
    };
  }

  const parsed = parseAbsoluteBaseUrl(value);
  if (isProduction && parsed.scheme !== 'https') {
    fail('INSECURE', 'production absolute URL must use https (use "/" for same-origin)');
  }

  return {
    origin: parsed.origin,
    clientBase: isProduction ? parsed.origin : '',
    proxyTarget: isProduction ? null : parsed.origin,
    isSameOrigin: false,
    isCompileOnlyPlaceholder: isCompileOnlyPlaceholder(parsed.hostname)
  };
}

function resolveClientBase(rawValue, mode) {
  return resolveConsoleEndpoints(rawValue, {mode: mode}).clientBase;
}

function joinServerPath(base, path) {
  const normalizedBase = trimTrailingSlash(readEnvValue(base));
  const suffix = readEnvValue(path);
  if (suffix === '') {
    return normalizedBase;
  }
  return normalizedBase + (suffix.startsWith('/') ? suffix : `/${suffix}`);
}

module.exports = {
  ENV_NAME,
  DEFAULT_DEVELOPMENT_BASE_URL,
  COMPILE_ONLY_PLACEHOLDER_HOST,
  SAME_ORIGIN_VALUE,
  EnvConfigError,
  readEnvValue,
  trimTrailingSlash,
  parseAbsoluteBaseUrl,
  resolveConsoleEndpoints,
  resolveClientBase,
  joinServerPath
};
